import axios from "axios";
import { getApiUrlWithVersion } from "./environmentService";
import { getAuthUser } from "./authService";
import {
  StrategyDocument,
  StrategyDocumentCreateResponse,
  StrategyDocumentJobStatus,
} from "../types/strategyDocument";

class StrategyDocumentService {
  async create(rawText: string): Promise<StrategyDocumentCreateResponse> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/strategy_documents`,
      { raw_text: rawText },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async pollStatus(jobId: string): Promise<StrategyDocumentJobStatus> {
    const response = await axios.get(
      `${getApiUrlWithVersion()}/strategy_documents/status/${jobId}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async get(id: number): Promise<StrategyDocument> {
    const response = await axios.get(
      `${getApiUrlWithVersion()}/strategy_documents/${id}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async createBlueprint(
    id: number,
    data: {
      name: string;
      audience: string;
      additional_details: string;
      cadence: { frequency: string; day_of_week: string };
      sections: Array<{
        title: string;
        content_type: string;
        format: string;
        instructions: string;
      }>;
    }
  ): Promise<any> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/strategy_documents/${id}/create_blueprint`,
      data,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async overwriteBlueprint(
    id: number,
    blueprintId: string,
    data: {
      name: string;
      audience: string;
      additional_details: string;
      cadence: { frequency: string; day_of_week: string };
      sections: Array<{
        title: string;
        content_type: string;
        format: string;
        instructions: string;
      }>;
    }
  ): Promise<any> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/strategy_documents/${id}/overwrite_blueprint`,
      { ...data, blueprint_id: blueprintId },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async list(): Promise<Array<{ id: number; name: string; audience: string | null; google_doc_url: string | null; status: string; created_at: string }>> {
    const response = await axios.get(
      `${getApiUrlWithVersion()}/strategy_documents`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async refreshFromGoogle(id: number, googleDocUrl?: string): Promise<{ id: number; job_id: string; google_doc_url: string; message: string }> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/strategy_documents/${id}/refresh_from_google`,
      googleDocUrl ? { google_doc_url: googleDocUrl } : {},
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  private getAuthHeaders(): object {
    return {
      Authorization: getAuthUser()?.accessToken,
    };
  }
}

const strategyDocumentService = new StrategyDocumentService();
export default strategyDocumentService;
